import { ChevronLeft, ChevronRight, Copy, Pause, Play, Plus, Square, Trash2 } from "lucide-react";
import type { JSX } from "react";

import type { Frame } from "./scene.js";

interface FrameStripProps {
  frames: Frame[];
  currentIndex: number;
  playing: boolean;
  /** Live progress in [0,1] of the current transition while playing. */
  progress?: number;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onDuplicate: (index: number) => void;
  onRemove: (index: number) => void;
  onSetDuration: (index: number, durationMs: number) => void;
  onTogglePlay: () => void;
  onStop?: () => void;
  /** Recording mode: every committed edit becomes a new frame. */
  recording: boolean;
  onToggleRecording: () => void;
  showTrails: boolean;
  onToggleTrails: (next: boolean) => void;
}

/**
 * Bottom strip listing all keyframes with thumbnails of token positions,
 * playback controls and a trail toggle. Each frame has a duration spinner so
 * coaches can pace transitions. A thin progress bar along the top edge
 * visualizes the live transition during playback. The record toggle next to
 * the transport controls turns on "Aufnahme" — each committed edit on the
 * board is then captured as its own new frame.
 */
export function FrameStrip({
  frames,
  currentIndex,
  playing,
  progress = 0,
  onSelect,
  onAdd,
  onDuplicate,
  onRemove,
  onSetDuration,
  onTogglePlay,
  onStop,
  recording,
  onToggleRecording,
  showTrails,
  onToggleTrails,
}: FrameStripProps): JSX.Element {
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < frames.length - 1;
  const overallProgress =
    frames.length > 1 ? Math.min(1, (currentIndex + progress) / Math.max(1, frames.length - 1)) : 0;
  return (
    <div className="pointer-events-auto relative flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-black/30 px-3 py-2 backdrop-blur">
      {/* Top progress bar — full strip width */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden rounded-t-2xl bg-white/5">
        <div
          className="h-full bg-pitch-lime"
          style={{ width: `${overallProgress * 100}%` }}
          aria-hidden
        />
      </div>

      <div className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-white/5 p-0.5">
        <button
          type="button"
          onClick={() => canPrev && onSelect(currentIndex - 1)}
          disabled={!canPrev}
          title="Vorheriger Frame (←)"
          className="rounded p-1.5 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={onTogglePlay}
          disabled={frames.length < 2}
          title={playing ? "Pause (Space)" : "Abspielen (Space)"}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-100 transition disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: playing ? "rgba(255,176,46,0.22)" : "rgba(151,252,0,0.22)",
          }}
        >
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        {onStop && (
          <button
            type="button"
            onClick={onStop}
            disabled={!playing && currentIndex === 0}
            title="Stop & Anfang"
            className="rounded p-1.5 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <Square size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={() => canNext && onSelect(currentIndex + 1)}
          disabled={!canNext}
          title="Nächster Frame (→)"
          className="rounded p-1.5 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <button
        type="button"
        onClick={onToggleRecording}
        disabled={playing}
        aria-pressed={recording}
        title={
          recording ? "Aufnahme beenden" : "Aufnahme: jede Änderung wird als neuer Frame angelegt"
        }
        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition disabled:cursor-not-allowed disabled:opacity-40 ${
          recording
            ? "border-rose-400/60 bg-rose-500/20 text-rose-100"
            : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
        }`}
      >
        <span className="relative flex h-2.5 w-2.5" aria-hidden>
          {recording && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 motion-safe:animate-ping" />
          )}
          <span
            className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
              recording ? "bg-rose-500" : "bg-rose-400/70"
            }`}
          />
        </span>
        Aufnahme
      </button>

      <label className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs text-white/80">
        <input
          type="checkbox"
          checked={showTrails}
          onChange={(e) => onToggleTrails(e.target.checked)}
          className="h-3.5 w-3.5 cursor-pointer accent-emerald-400"
        />
        Laufwege
      </label>

      <div className="mx-1 h-6 w-px bg-white/15" aria-hidden />

      <div className="flex max-w-full flex-1 items-center gap-2 overflow-x-auto pb-1">
        {frames.map((frame, i) => {
          const active = i === currentIndex;
          return (
            <div
              key={frame.id}
              className={`relative flex shrink-0 items-center gap-2 rounded-xl border px-4 py-1 transition ${
                active
                  ? "border-emerald-400/70 bg-emerald-500/15 shadow-[0_0_0_1px_rgba(151,252,0,0.25)]"
                  : "border-white/15 bg-white/5 hover:bg-white/10"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(i)}
                className="flex flex-col items-start gap-0.5 text-left"
              >
                <span className="text-[11px] font-medium text-white">Frame {i + 1}</span>
              </button>
              {i > 0 && (
                <label className="flex items-center gap-1 text-[10px] text-white/55">
                  <input
                    type="number"
                    min={100}
                    step={100}
                    value={frame.durationMs}
                    onChange={(e) => onSetDuration(i, Number(e.target.value) || 100)}
                    className="w-16 rounded bg-black/40 px-1 py-0.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-emerald-400/50"
                  />
                  ms
                </label>
              )}
              <button
                type="button"
                onClick={() => onDuplicate(i)}
                title="Frame duplizieren"
                className="rounded p-1 text-white/55 hover:bg-white/10 hover:text-white"
              >
                <Copy size={12} />
              </button>
              {frames.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  title="Frame löschen"
                  className="rounded p-1 text-white/55 hover:bg-rose-500/30 hover:text-rose-100"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAdd}
        className="flex shrink-0 items-center gap-1 rounded-lg border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs text-white transition hover:bg-white/20"
      >
        <Plus size={14} />
        Frame
      </button>
    </div>
  );
}
